import {
    Vector3,
} from '@babylonjs/core/Maths/math.vector';

import { Color3 } from '@babylonjs/core/Maths/math.color';

import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';

import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';

import { TextBlock } from '@babylonjs/gui/2D/controls/textBlock';

import * as ThreeUtilities from './threeutilities';

// Makes unique line ids.
let lineId = 1;

export default {
    methods: {
        loadDimensions() {
            let hasDimensions = false;

            if (this.dimensionResources) {
                this.dimensionResources.forEach((d) => {
                    this.destroyDimension(d);
                });
            }

            this.dimensionResources = [];

            this.forAllControllers((controller, id) => {
                if (controller.blueprint.dimensions == null) {
                    return;
                }

                // Check for components with dimensions.
                controller.state.forEach((s) => {
                    // Placeholders do not have dimensions.
                    if (s.component == null || s.component.placeholder) {
                        return;
                    }

                    // No dimensions.
                    if (s.component.dimensions == null) {
                        return;
                    }

                    s.component.dimensions.forEach((d) => {
                        if (d.value == null || d.value.length === 0) {
                            return;
                        }

                        const definition = controller.blueprint.dimensions.find((x) => x.code === d.code);

                        if (definition == null || definition.custom == null) {
                            return;
                        }

                        const key = `${id}->${s.component.code}->${d.code}`;

                        hasDimensions = true;

                        if (this.showDimensions) {
                            if (definition.custom['three-show-as'] === 'lines') {
                                const resources = this.addDimensionLines(controller, key, s, d, definition);

                                if (resources) {
                                    this.dimensionResources.push(resources);
                                }
                            }
                        }
                    });
                });
            });

            this.emitter.$emit('setHasDimensions', hasDimensions);
        },

        destroyDimension(d) {
            if (d.meshes) {
                d.meshes.forEach((m) => {
                    m.dispose(true, true);
                });
            }

            if (d.materials) {
                d.materials.forEach((m) => {
                    m.dispose();
                });
            }

            if (d.gui) {
                d.gui.forEach((g) => {
                    this.gui.removeControl(g);
                });
            }
        },

        addDimensionLines(controller, key, state, dimension, definition) {
            const placementKey = this.placementKey(controller, state.placement.code);

            const placement = this.placements[placementKey];

            if (placement == null || placement.meshes == null || placement.meshes.length < 1) {
                return null;
            }

            let min = null;
            let max = null;

            placement.meshes.forEach((m) => {
                if (m.mesh == null) {
                    return;
                }

                const info = m.mesh.getBoundingInfo();

                if (info == null || info.boundingBox == null) {
                    return;
                }

                if (min == null) {
                    min = info.boundingBox.minimum;
                    max = info.boundingBox.maximum;
                } else {
                    min = Vector3.Minimize(min, info.boundingBox.minimum);
                    max = Vector3.Maximize(max, info.boundingBox.maximum);
                }
            });

            if (min == null) {
                return null;
            }

            const start = ThreeUtilities.toVector3(definition.custom['three-location-start'], true);
            const end = ThreeUtilities.toVector3(definition.custom['three-location-end'], true);
            let scale = +(definition.custom['three-location-scale'] || '1');

            if (scale == null) {
                scale = 1;
            }

            start.scaleInPlace(scale);
            end.scaleInPlace(scale);

            const c = max.add(min).multiplyByFloats(0.5, 0.5, 0.5);
            const extent = max.subtract(c);

            const lineStart = c.add(extent.multiply(start));
            const lineEnd = c.add(extent.multiply(end));

            const lineCenter = lineStart.add(lineEnd).multiplyByFloats(0.5, 0.5, 0.5);

            const alongActual = lineEnd.subtract(lineStart);
            const along = alongActual.clone().normalize();
            let up = Vector3.Up();

            let normal = Vector3.Cross(along, up);
            if (normal.length() === 0) {
                // Try another axis.
                up = new Vector3(1, 0, 0);

                normal = Vector3.Cross(up, along);
            }

            up = Vector3.Cross(normal, along);

            const lineScale = this.sizePerTenPixels(this.camera.radius) * 0.1;

            const lineWidth = +(definition.custom['three-line-width'] || '5') * 0.5;
            const lineOffset = +(definition.custom['three-line-offset'] || '5');
            const capWidth = +(definition.custom['three-cap-width'] || '0') * 0.5;
            const capLength = +(definition.custom['three-cap-length'] || '0');

            // TODO: Take local scaling into the account.
            const lineMaterial = new PBRMaterial(`${key}->material`, this.scene);

            const lineColor = ThreeUtilities.toColor4(definition.custom['three-line-color']);

            lineMaterial.albedoColor = lineColor;
            lineMaterial.emissiveColor = lineColor;
            lineMaterial.reflectivityColor = Color3.Black();
            lineMaterial.backFaceCulling = false;

            // Estimate the size of the text box.
            let characters = (dimension.value || '').toString().length;
            // Add padding.
            characters += 2;

            let fontSize = +(definition.custom['three-label-font-size'] || '10');
            // Wild guess - assign 0.8 m-height to width ratio.
            fontSize *= 0.8;

            const verticalAngle = Math.abs(Vector3.Dot(Vector3.Up(), along));

            let textWidth;
            if (verticalAngle > 0.9) {
                textWidth = fontSize * 1.5 * lineScale;
            } else {
                textWidth = characters * fontSize * lineScale;
            }

            // Split the arrow in two parts.
            const lineLength = alongActual.length();
            let sideLength = ((lineLength - textWidth) * 0.5) - (lineOffset * lineScale);

            if (sideLength < 0) {
                sideLength = 0;
            }

            const arrowStartMesh = this.makeLine(
                placementKey,
                this.makePoint(lineStart, along, lineOffset, lineScale),
                this.makePoint(lineStart, along, sideLength, 1),
                along,
                normal,
                up,
                lineScale,
                lineWidth,
                capWidth,
                capLength,
            );
            arrowStartMesh.material = lineMaterial;
            arrowStartMesh.parent = placement.offset;

            const arrowEndMesh = this.makeLine(
                placementKey,
                this.makePoint(lineEnd, along, -lineOffset, lineScale),
                this.makePoint(lineEnd, along, -sideLength, 1),
                along.negate(),
                normal,
                up,
                lineScale,
                lineWidth,
                capWidth,
                capLength,
            );
            arrowEndMesh.material = lineMaterial;
            arrowEndMesh.parent = placement.offset;

            const arrowCenterMesh = this.makeLine(placementKey, lineCenter, lineCenter, along, normal, up, 0, lineWidth, capWidth, capLength);
            arrowCenterMesh.material = lineMaterial;
            arrowCenterMesh.parent = placement.offset;

            // Add a label.
            const labelSize = (+(definition.custom['three-label-font-size'] || '10')) * 2;

            const label = new TextBlock();
            label.text = `${dimension.value} ${definition.unit}`;
            label.paddingLeft = 0;
            label.paddingRight = 0;
            label.color = definition.custom['three-label-color'] || '#ffffff';
            label.fontSize = `${labelSize}px`;
            label.fontFamily = definition.custom['three-label-font'] || 'Arial';
            label.resizeToFit = true;
            this.gui.addControl(label);

            label.linkWithMesh(arrowCenterMesh);
            label.linkOffsetY = 0;
            label.linkOffsetX = 0;

            return {
                materials: [lineMaterial],
                meshes: [arrowStartMesh, arrowEndMesh, arrowCenterMesh],
                gui: [label],
            };
        },

        addTriangle(positions, indices, a, b, c) {
            positions.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);

            const l = indices.length;

            indices.push(l + 0, l + 1, l + 2);
        },

        makePoint(x, deltaA, scaleA, scale) {
            return new Vector3(
                x.x + (deltaA.x * scaleA * scale),
                x.y + (deltaA.y * scaleA * scale),
                x.z + (deltaA.z * scaleA * scale),
            );
        },

        makeLine(parentId, from, to, along, normal, up, lineScale, lineWidth, capWidth, capLength) {
            lineId += 1;

            const id = `${parentId}->line->${lineId}`;

            const mesh = new Mesh(id, this.scene);

            const positions = [];
            const indices = [];

            const l = to.subtract(from).length();

            const mainLength = l - (capLength * lineScale);

            // Arrow cap.
            const capEnd = this.makePoint(from, along, capLength, lineScale);

            this.addTriangle(
                positions,
                indices,
                from,
                this.makePoint(capEnd, normal, capWidth, lineScale),
                this.makePoint(capEnd, up, capWidth, lineScale),
            );

            this.addTriangle(
                positions,
                indices,
                from,
                this.makePoint(capEnd, up, capWidth, lineScale),
                this.makePoint(capEnd, normal, -capWidth, lineScale),
            );

            this.addTriangle(
                positions,
                indices,
                from,
                this.makePoint(capEnd, normal, -capWidth, lineScale),
                this.makePoint(capEnd, up, -capWidth, lineScale),
            );

            this.addTriangle(
                positions,
                indices,
                from,
                this.makePoint(capEnd, up, -capWidth, lineScale),
                this.makePoint(capEnd, normal, capWidth, lineScale),
            );

            // Close the arrow.

            this.addTriangle(
                positions,
                indices,
                capEnd,
                this.makePoint(capEnd, normal, capWidth, lineScale),
                this.makePoint(capEnd, up, capWidth, lineScale),
            );

            this.addTriangle(
                positions,
                indices,
                capEnd,
                this.makePoint(capEnd, up, capWidth, lineScale),
                this.makePoint(capEnd, normal, -capWidth, lineScale),
            );

            this.addTriangle(
                positions,
                indices,
                capEnd,
                this.makePoint(capEnd, normal, -capWidth, lineScale),
                this.makePoint(capEnd, up, -capWidth, lineScale),
            );

            this.addTriangle(
                positions,
                indices,
                capEnd,
                this.makePoint(capEnd, up, -capWidth, lineScale),
                this.makePoint(capEnd, normal, capWidth, lineScale),
            );

            if (mainLength > 0) {
                // Body of the arrow.

                this.addTriangle(
                    positions,
                    indices,
                    this.makePoint(capEnd, up, -lineWidth, lineScale),
                    this.makePoint(to, up, -lineWidth, lineScale),
                    this.makePoint(capEnd, normal, lineWidth, lineScale),
                );

                this.addTriangle(
                    positions,
                    indices,
                    this.makePoint(capEnd, normal, lineWidth, lineScale),
                    this.makePoint(to, up, -lineWidth, lineScale),
                    this.makePoint(to, normal, lineWidth, lineScale),
                );

                this.addTriangle(
                    positions,
                    indices,
                    this.makePoint(capEnd, normal, lineWidth, lineScale),
                    this.makePoint(to, normal, lineWidth, lineScale),
                    this.makePoint(capEnd, up, lineWidth, lineScale),
                );

                this.addTriangle(
                    positions,
                    indices,
                    this.makePoint(capEnd, up, lineWidth, lineScale),
                    this.makePoint(to, normal, lineWidth, lineScale),
                    this.makePoint(to, up, lineWidth, lineScale),
                );

                this.addTriangle(
                    positions,
                    indices,
                    this.makePoint(capEnd, up, lineWidth, lineScale),
                    this.makePoint(to, up, lineWidth, lineScale),
                    this.makePoint(capEnd, normal, -lineWidth, lineScale),
                );

                this.addTriangle(
                    positions,
                    indices,
                    this.makePoint(capEnd, normal, -lineWidth, lineScale),
                    this.makePoint(to, up, lineWidth, lineScale),
                    this.makePoint(to, normal, -lineWidth, lineScale),
                );

                this.addTriangle(
                    positions,
                    indices,
                    this.makePoint(capEnd, up, -lineWidth, lineScale),
                    this.makePoint(to, up, -lineWidth, lineScale),
                    this.makePoint(capEnd, normal, lineWidth, lineScale),
                );

                this.addTriangle(
                    positions,
                    indices,
                    this.makePoint(capEnd, normal, lineWidth, lineScale),
                    this.makePoint(to, up, -lineWidth, lineScale),
                    this.makePoint(to, normal, lineWidth, lineScale),
                );

                // Close the start end.

                this.addTriangle(
                    positions,
                    indices,
                    capEnd,
                    this.makePoint(capEnd, normal, lineWidth, lineScale),
                    this.makePoint(capEnd, up, lineWidth, lineScale),
                );

                this.addTriangle(
                    positions,
                    indices,
                    capEnd,
                    this.makePoint(capEnd, up, lineWidth, lineScale),
                    this.makePoint(capEnd, normal, -lineWidth, lineScale),
                );

                this.addTriangle(
                    positions,
                    indices,
                    capEnd,
                    this.makePoint(capEnd, normal, -lineWidth, lineScale),
                    this.makePoint(capEnd, up, -lineWidth, lineScale),
                );

                this.addTriangle(
                    positions,
                    indices,
                    capEnd,
                    this.makePoint(capEnd, up, -lineWidth, lineScale),
                    this.makePoint(capEnd, normal, lineWidth, lineScale),
                );

                // Close the tail end.

                this.addTriangle(
                    positions,
                    indices,
                    to,
                    this.makePoint(to, normal, lineWidth, lineScale),
                    this.makePoint(to, up, lineWidth, lineScale),
                );

                this.addTriangle(
                    positions,
                    indices,
                    to,
                    this.makePoint(to, up, lineWidth, lineScale),
                    this.makePoint(to, normal, -lineWidth, lineScale),
                );

                this.addTriangle(
                    positions,
                    indices,
                    to,
                    this.makePoint(to, normal, -lineWidth, lineScale),
                    this.makePoint(to, up, -lineWidth, lineScale),
                );

                this.addTriangle(
                    positions,
                    indices,
                    to,
                    this.makePoint(to, up, -lineWidth, lineScale),
                    this.makePoint(to, normal, lineWidth, lineScale),
                );
            }

            const vertexData = new VertexData();

            vertexData.positions = positions;
            vertexData.indices = indices;

            vertexData.applyToMesh(mesh);

            return mesh;
        },
    },
};
